import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Home, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  useDashboardOverview,
  useDashboardTimeseries,
  useDashboardSignupsByLocation,
  useDashboardSignupsByRegion,
  useDashboardSignupMethods,
  useDashboardTopCreators,
  useDashboardActivityHeatmap,
  resyncDashboardPostHog,
  POSTHOG_RESYNC_COOLDOWN_MS,
} from '../api';
import { useAdminPageFilters } from '@/hooks/useAdminPageFilters';
import {
  boundsForTimeRange,
  daysForTimeRange,
  shouldShowPriorComparison,
} from '@/lib/timeRangeOptions';
import {
  DashboardChartFilters,
  formatPriorComparisonLabel,
} from '../components/DashboardFilters';
import { DashboardKpiCard } from '../components/DashboardKpiCard';
import { DashboardPlatformGrowthChart } from '../components/DashboardPlatformGrowthChart';
import { DashboardAudienceDonut } from '../components/DashboardAudienceDonut';
import { DashboardAccountOverviewChart } from '../components/DashboardAccountOverviewChart';
import {
  DEFAULT_DASHBOARD_TIMEZONE,
  HEATMAP_TIMEZONES,
} from '@/lib/heatmapTimezones';
import { DashboardActivityHeatmap } from '../components/DashboardActivityHeatmap';
import { DashboardSignupsByLocationCard } from '../components/DashboardSignupsByLocationCard';
import { DashboardSignupMethodsCard } from '../components/DashboardSignupMethodsCard';
import { DashboardTopCreatorsCard } from '../components/DashboardTopCreatorsCard';
import { buildDashboardKpis, timeseriesSeriesValues } from '../dashboardKpis';

const RESYNC_COOLDOWN_STORAGE_KEY = 'tipmi.dashboard.posthogResyncCooldownUntil';

function readStoredCooldownUntil(): number {
  try {
    const raw = sessionStorage.getItem(RESYNC_COOLDOWN_STORAGE_KEY);
    if (!raw) return 0;
    const until = Number(raw);
    return Number.isFinite(until) ? until : 0;
  } catch {
    return 0;
  }
}

function formatCooldown(ms: number): string {
  const totalSec = Math.max(1, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m <= 0) return `${s}s`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function queryBusy(query: {
  isError: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
}): boolean {
  return !query.isError && (query.isPending || query.isPlaceholderData);
}

/**
 * TipMi Admin Dashboard — Figma packing.
 * Date + Country filters appear in the page header and on Platform Growth
 * Overview; both share page state (`?range=&country=&timezone=` for deep links)
 * and apply to the whole page.
 */
export function DashboardPage() {
  const queryClient = useQueryClient();
  const {
    range,
    setRange,
    countryCode,
    setCountryCode,
    timezone: heatmapTimezone,
    setTimezone: setHeatmapTimezone,
  } = useAdminPageFilters({
    defaultTimezone: DEFAULT_DASHBOARD_TIMEZONE,
    allowedTimezones: HEATMAP_TIMEZONES.map((zone) => zone.value),
  });
  const [resyncing, setResyncing] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(readStoredCooldownUntil);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const bounds = boundsForTimeRange(range);
  const days = bounds.days ?? daysForTimeRange(range);
  const chartDays = days ?? 30;
  const country = countryCode || undefined;

  const cooldownRemainingMs = Math.max(0, cooldownUntil - nowMs);
  const onCooldown = cooldownRemainingMs > 0;

  useEffect(() => {
    if (!onCooldown) {
      if (cooldownUntil > 0 && cooldownUntil <= Date.now()) {
        try {
          sessionStorage.removeItem(RESYNC_COOLDOWN_STORAGE_KEY);
        } catch {
          // ignore
        }
        if (cooldownUntil !== 0) setCooldownUntil(0);
      }
      return;
    }
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [onCooldown, cooldownUntil]);

  function startResyncCooldown(durationMs = POSTHOG_RESYNC_COOLDOWN_MS) {
    const until = Date.now() + durationMs;
    setCooldownUntil(until);
    setNowMs(Date.now());
    try {
      sessionStorage.setItem(RESYNC_COOLDOWN_STORAGE_KEY, String(until));
    } catch {
      // ignore storage failures
    }
  }

  const overviewQuery = useDashboardOverview(bounds, country);
  const { data, isError } = overviewQuery;
  const timeseriesQuery = useDashboardTimeseries(chartDays, country);
  const locationQuery = useDashboardSignupsByLocation(days, country);
  const regionQuery = useDashboardSignupsByRegion(days, country);
  const methodsQuery = useDashboardSignupMethods(days, country);
  const topCreatorsQuery = useDashboardTopCreators(5, country);
  const heatmapQuery = useDashboardActivityHeatmap(days, country, heatmapTimezone);

  const overviewBusy = queryBusy(overviewQuery);
  const timeseriesBusy = queryBusy(timeseriesQuery);
  const heatmapBusy =
    !heatmapQuery.isError &&
    (queryBusy(heatmapQuery) || heatmapQuery.data?.timezone !== heatmapTimezone);

  const showPriorComparison = shouldShowPriorComparison(range);
  const { topRow, bottomRow } = data
    ? (() => {
        const built = buildDashboardKpis(data, timeseriesQuery.data);
        if (showPriorComparison) return built;
        return {
          topRow: built.topRow.map((card) => ({ ...card, delta: null })),
          bottomRow: built.bottomRow.map((card) => ({ ...card, delta: null })),
        };
      })()
    : { topRow: [], bottomRow: [] };
  const hasCards = topRow.length > 0 || bottomRow.length > 0;

  const filterProps = {
    range,
    onRangeChange: setRange,
    countryCode,
    onCountryCodeChange: setCountryCode,
    availableCountryCodes: data?.availableCountryCodes ?? [],
    ...(!overviewBusy && data?.startDate ? { startDate: data.startDate } : {}),
    ...(!overviewBusy && data?.endDate ? { endDate: data.endDate } : {}),
  };

  const categories = useMemo(
    () => timeseriesQuery.data?.categories ?? [],
    [timeseriesQuery.data?.categories],
  );
  const newSignups = useMemo(
    () => timeseriesSeriesValues(timeseriesQuery.data, 'NEW_USERS'),
    [timeseriesQuery.data],
  );
  const newCreators = useMemo(
    () => timeseriesSeriesValues(timeseriesQuery.data, 'NEW_CREATORS'),
    [timeseriesQuery.data],
  );
  const activeUsers = useMemo(
    () => timeseriesSeriesValues(timeseriesQuery.data, 'ACTIVE_USERS'),
    [timeseriesQuery.data],
  );
  const deletedAccounts = useMemo(
    () => timeseriesSeriesValues(timeseriesQuery.data, 'DELETED_ACCOUNTS'),
    [timeseriesQuery.data],
  );
  const visitorsSeries = useMemo(
    () => timeseriesSeriesValues(timeseriesQuery.data, 'VISITORS'),
    [timeseriesQuery.data],
  );

  const audienceSlices = useMemo(
    () => [
      {
        key: 'visitors' as const,
        label: 'Visitors',
        value: data?.visitors ?? null,
        comingSoon: data?.visitorsStatus !== 'AVAILABLE',
      },
      {
        key: 'users' as const,
        label: 'Users',
        value: data?.totalUsers ?? null,
      },
      {
        key: 'creators' as const,
        label: 'Creators',
        value: data?.totalCreators ?? null,
      },
    ],
    [data],
  );

  const growthTotal =
    (data?.totalUsers ?? 0) + (data?.totalCreators ?? 0) || null;
  const growthDelta =
    data?.totalUsersDeltaPercent != null || data?.totalCreatorsDeltaPercent != null
      ? Number(
          (
            (Number(data?.totalUsersDeltaPercent ?? 0) +
              Number(data?.totalCreatorsDeltaPercent ?? 0)) /
            ((data?.totalUsersDeltaPercent != null ? 1 : 0) +
              (data?.totalCreatorsDeltaPercent != null ? 1 : 0) || 1)
          ).toFixed(1),
        )
      : null;

  // TM-3608: All the time has no meaningful prior period — omit % and “vs …”.
  const comparisonLabel =
    !overviewBusy && showPriorComparison && days != null
      ? (formatPriorComparisonLabel(data?.startDate, data?.endDate) ?? undefined)
      : undefined;

  async function onResyncPostHog() {
    if (onCooldown || resyncing) return;
    setResyncing(true);
    try {
      const result = await resyncDashboardPostHog({
        days: chartDays,
        ...(country ? { countryCode: country } : {}),
        timezone: heatmapTimezone,
        includeToday: true,
      });
      if (result.status === 'RATE_LIMITED') {
        startResyncCooldown();
        toast.message('Resync cooldown', {
          description: result.message ?? 'Wait a couple of minutes and try again.',
        });
        return;
      }
      if (result.status !== 'OK') {
        toast.error('PostHog resync failed', {
          description: result.message ?? result.status,
        });
        return;
      }
      startResyncCooldown();
      await queryClient.invalidateQueries({ queryKey: ['/admin/api/dashboard'] });
      await Promise.all([
        timeseriesQuery.refetch(),
        heatmapQuery.refetch(),
        queryClient.invalidateQueries({ queryKey: ['/admin/api/dashboard/overview'] }),
      ]);
      toast.success('PostHog data refreshed', {
        description: `Daily ${result.dailyRowsUpserted ?? 0} · windows ${result.windowRowsUpserted ?? 0} · heatmap ${result.heatmapRowsUpserted ?? 0}`,
      });
    } catch (err) {
      toast.error('PostHog resync failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setResyncing(false);
    }
  }

  const resyncLabel = resyncing
    ? 'Resyncing…'
    : onCooldown
      ? `Resync in ${formatCooldown(cooldownRemainingMs)}`
      : 'Resync PostHog';
  const resyncTitle = onCooldown
    ? `Cooldown active — available again in ${formatCooldown(cooldownRemainingMs)}. Protects PostHog Query rate limits.`
    : 'Re-query PostHog for the selected range and refresh Visitors, Active Users, and the activity heatmap stored in MySQL. Rate-limited (~2 minutes).';

  return (
    <div className="space-y-4 p-6 md:p-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Home className="size-5 text-muted-foreground" aria-hidden />
          <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <DashboardChartFilters {...filterProps} />
          <HoverHint text={resyncTitle}>
            <Button
              type="button"
              variant="outline"
              className="h-auto rounded-full border-zinc-700 bg-zinc-900/80 px-4 py-2 text-zinc-100 shadow-none hover:bg-zinc-800 hover:text-zinc-50 disabled:opacity-60"
              disabled={resyncing || onCooldown}
              aria-label={resyncLabel}
              onClick={() => void onResyncPostHog()}
            >
              {resyncing ? (
                <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-2 size-4" aria-hidden />
              )}
              {resyncLabel}
            </Button>
          </HoverHint>
          <Button
            type="button"
            variant="outline"
            className="h-auto rounded-full border-zinc-700 bg-zinc-900/80 px-4 py-2 text-zinc-100 shadow-none hover:bg-zinc-800 hover:text-zinc-50"
            onClick={() =>
              toast.message('Export / Reports', {
                description: 'Export is planned for a later phase.',
              })
            }
          >
            Export / Reports
            <ChevronDown className="ml-2 size-4 text-muted-foreground" aria-hidden />
          </Button>
        </div>
      </header>

      {overviewQuery.isPending && !data ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : isError && !data ? (
        <p className="text-sm text-destructive">Failed to load dashboard overview.</p>
      ) : !hasCards && !overviewBusy ? (
        <p className="text-sm text-muted-foreground">No dashboard data available.</p>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(300px,1fr)]">
          <div className="min-w-0 space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {overviewBusy ? (
                <div className="col-span-full flex h-[132px] items-center justify-center rounded-xl bg-[#2a2a2e] text-sm text-muted-foreground">
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Loading metrics…
                </div>
              ) : (
                topRow.map((card) => (
                  <DashboardKpiCard
                    key={card.label}
                    {...card}
                    {...(comparisonLabel ? { comparisonLabel } : {})}
                  />
                ))
              )}
            </div>

            <DashboardPlatformGrowthChart
              categories={categories}
              newSignups={newSignups}
              newCreators={newCreators}
              visitorsSeries={visitorsSeries}
              visitorsComingSoon={data?.visitorsStatus !== 'AVAILABLE'}
              isLoading={timeseriesBusy}
              growthTotal={overviewBusy ? null : growthTotal}
              growthDelta={overviewBusy || !showPriorComparison ? null : growthDelta}
              {...filterProps}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {overviewBusy ? (
                <div className="col-span-full flex h-[132px] items-center justify-center rounded-xl bg-[#2a2a2e] text-sm text-muted-foreground">
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Loading metrics…
                </div>
              ) : (
                bottomRow.map((card) => (
                  <DashboardKpiCard
                    key={card.label}
                    {...card}
                    {...(comparisonLabel ? { comparisonLabel } : {})}
                  />
                ))
              )}
            </div>

            <DashboardAccountOverviewChart
              categories={categories}
              activeUsers={activeUsers}
              newSignups={newSignups}
              deletedAccounts={deletedAccounts}
              isLoading={timeseriesBusy}
            />

            <DashboardActivityHeatmap
              {...(!heatmapBusy &&
              heatmapQuery.data != null &&
              heatmapQuery.data.timezone === heatmapTimezone
                ? { data: heatmapQuery.data }
                : {})}
              isLoading={heatmapBusy}
              timezone={heatmapTimezone}
              onTimezoneChange={setHeatmapTimezone}
            />
          </div>

          <aside className="min-w-0 space-y-4">
            <DashboardAudienceDonut slices={audienceSlices} isLoading={overviewBusy} />
            <DashboardSignupsByLocationCard
              items={
                showPriorComparison
                  ? (locationQuery.data?.items ?? [])
                  : (locationQuery.data?.items ?? []).map((item) => ({
                      ...item,
                      deltaPercent: null,
                    }))
              }
              {...(locationQuery.data?.total != null ? { total: locationQuery.data.total } : {})}
              isLoading={queryBusy(locationQuery)}
              note="Completed signups by signup country (IP at registration). Missing snapshot usually appears as Unknown."
              hint="Grouped by signup country — the country from their IP when they completed registration. Not Stripe Connect country. Incomplete signups are excluded. Missing signup country shows as Unknown."
            />
            <DashboardSignupsByLocationCard
              title="Top States"
              items={
                showPriorComparison
                  ? (regionQuery.data?.items ?? [])
                  : (regionQuery.data?.items ?? []).map((item) => ({
                      ...item,
                      deltaPercent: null,
                    }))
              }
              {...(regionQuery.data?.total != null ? { total: regionQuery.data.total } : {})}
              isLoading={Boolean(country) && queryBusy(regionQuery)}
              note={
                country
                  ? 'Completed signups by signup state/region (IP at registration). Missing region appears as Unknown.'
                  : 'Select a country to see Top States by signup region.'
              }
              hint={
                country
                  ? 'Grouped by signup state/region — the state or region from their IP when they completed registration, for the selected signup country. Incomplete signups are excluded. Missing region shows as Unknown.'
                  : 'Select a country first. Then Top States shows signup state/region (from IP at registration) for that signup country.'
              }
              emptyMessage={
                country
                  ? 'No signup-state data in this period.'
                  : 'Select a country to see Top States.'
              }
            />
            <DashboardSignupMethodsCard
              items={methodsQuery.data?.items ?? []}
              {...(methodsQuery.data?.total != null ? { total: methodsQuery.data.total } : {})}
              isLoading={queryBusy(methodsQuery)}
            />
            <DashboardTopCreatorsCard
              creators={topCreatorsQuery.data?.items ?? []}
              isLoading={queryBusy(topCreatorsQuery)}
            />
          </aside>
        </div>
      )}
    </div>
  );
}

/**
 * Immediate hover/focus explanation. Native `title` is delayed and never
 * appears on this button while it is disabled (`pointer-events-none`).
 */
function HoverHint({ text, children }: { text: string; children: ReactNode }) {
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const place = useCallback((el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const half = 140;
    const x = Math.min(
      Math.max(rect.left + rect.width / 2, half + 8),
      window.innerWidth - half - 8,
    );
    setPos({ x, y: rect.bottom });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  return (
    <>
      <span
        className="inline-flex outline-none"
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onMouseMove={(event) => place(event.currentTarget)}
        onMouseLeave={() => setOpen(false)}
        onFocus={(event) => {
          place(event.currentTarget);
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
      >
        {children}
      </span>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tipId}
              role="tooltip"
              className="pointer-events-none fixed z-[100] max-w-[280px] -translate-x-1/2 translate-y-2 rounded-md px-3 py-2 text-left shadow-lg"
              style={{ left: pos.x, top: pos.y, backgroundColor: '#1e2a44' }}
            >
              <p className="text-[12px] leading-4 text-white">{text}</p>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
